import React from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { 
  Car, Shield, FileText, Calculator, CircleDollarSign, Truck,
  ChevronRight, Phone, MessageCircle, CheckCircle
} from "lucide-react";

const LeistungenPage = () => {
  const services = [
    {
      id: "unfallgutachten",
      icon: Car,
      title: "Unfallgutachten",
      description: "Präzise Bewertung des Schadensumfangs, der Reparaturkosten und des Wertverlusts nach einem Unfall. Grundlage für Ihre Ansprüche gegenüber der Versicherung.",
      features: ["Detaillierte Schadensaufnahme", "Berechnung Wiederbeschaffungs- & Restwert", "Ermittlung Reparaturkosten", "Wertminderung"],
      image: "https://static.prod-images.emergentagent.com/jobs/4aee379d-d7a9-43ea-b5e1-4f4900c746b3/images/241b08b6be6c51d60ef3537157386b10c00116192a882dcf3ea6bfb08740a08c.png"
    },
    {
      id: "haftpflichtgutachten",
      icon: Shield,
      title: "Haftpflichtgutachten",
      description: "Detaillierte Dokumentation bei unverschuldetem Unfall zur Durchsetzung Ihrer Ansprüche gegenüber der gegnerischen Versicherung.",
      features: ["Kostenübernahme durch Gegner", "Unabhängige Bewertung", "Rechtssichere Dokumentation", "Direkte Versicherungskommunikation"],
      image: null
    },
    {
      id: "kasko-schaden",
      icon: FileText,
      title: "Kasko-Schaden",
      description: "Professionelle Gutachten für Schäden, die über Ihre Teil- oder Vollkaskoversicherung reguliert werden.",
      features: ["Teilkasko-Schäden", "Vollkasko-Schäden", "Hagel- & Sturmschäden", "Diebstahl-Dokumentation"],
      image: null
    },
    {
      id: "kostenvoranschlag",
      icon: Calculator,
      title: "Kostenvoranschlag",
      description: "Transparente und genaue Kostenvoranschläge für Kfz-Reparaturen zur optimalen Entscheidungsfindung und Kostenplanung.",
      features: ["Detaillierte Aufstellung", "Ersatzteil-Kalkulation", "Arbeitszeit-Berechnung", "Vergleichsgrundlage"],
      image: null
    },
    {
      id: "wertgutachten",
      icon: CircleDollarSign,
      title: "Wertgutachten",
      description: "Präzise Bestimmung des aktuellen Marktwerts Ihres Fahrzeugs für Kauf, Verkauf oder Versicherungszwecke.",
      features: ["Kauf-/Verkaufsberatung", "Versicherungseinstufung", "Leasingrückgabe", "Erbschafts-/Scheidungsfälle"],
      image: null
    },
    {
      id: "oldtimer-gutachten",
      icon: Truck,
      title: "Oldtimer-Gutachten",
      description: "Detaillierte Bewertung des tatsächlichen Wertes Ihres Klassikers durch erfahrene Experten mit Spezialkenntnissen.",
      features: ["Zustandsbewertung (Note 1-5)", "Marktwertermittlung", "H-Kennzeichen Vorbereitung", "Versicherungsgutachten"],
      image: "https://static.prod-images.emergentagent.com/jobs/4aee379d-d7a9-43ea-b5e1-4f4900c746b3/images/8c89ea046e5549062db9feaae22f3557075674a119a664b60ada39c1479e4929.png"
    }
  ];

  return (
    <>
      <SEO 
        title="Leistungen - Kfz-Gutachten & Sachverständiger"
        description="Professionelle Kfz-Gutachten in Hamburg & Schleswig-Holstein: Unfallgutachten, Haftpflichtgutachten, Wertgutachten, Oldtimer-Bewertung & Kostenvoranschläge. Unabhängig & objektiv."
        canonical="/leistungen"
        keywords="Kfz Gutachten, Unfallgutachten, Wertgutachten, Haftpflichtgutachten, Oldtimer Gutachten, Kostenvoranschlag"
      />
      {/* Hero */}
      <section className="bg-[#F7F7F7] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block text-[#AA6622] font-bold text-sm uppercase tracking-wider mb-4">Unsere Leistungen</span>
            <h1 className="text-4xl sm:text-5xl font-black text-black mb-6 font-['Outfit']">
              Professionelle Kfz-Gutachten für jeden Bedarf
            </h1>
            <p className="text-lg text-[#4A4A4A] leading-relaxed mb-8">
              Von der Unfallbegutachtung bis zur Oldtimer-Bewertung – wir bieten Ihnen das komplette Spektrum professioneller Kfz-Sachverständigenleistungen. Unabhängig, objektiv und immer in Ihrem Interesse.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="tel:+491748936514" className="bg-[#AA6622] text-white px-6 py-3 rounded-sm font-bold flex items-center justify-center space-x-2 hover:bg-[#8e541b] transition-colors">
                <Phone className="w-5 h-5" /><span>Jetzt anrufen</span>
              </a>
              <a href="https://wa.me/+491748936514" target="_blank" rel="noopener noreferrer" className="border-2 border-black text-black px-6 py-3 rounded-sm font-bold flex items-center justify-center space-x-2 hover:bg-black hover:text-white transition-colors">
                <MessageCircle className="w-5 h-5" /><span>WhatsApp</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {services.map((service, index) => (
              <div 
                key={service.id} 
                id={service.id}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
                data-testid={`service-detail-${service.id}`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="w-16 h-16 bg-[#AA6622] rounded-sm flex items-center justify-center mb-6">
                    <service.icon className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-3xl font-bold text-black mb-4 font-['Outfit']">{service.title}</h2>
                  <p className="text-lg text-[#4A4A4A] mb-6 leading-relaxed">{service.description}</p>
                  <ul className="space-y-3 mb-8">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-center space-x-3">
                        <CheckCircle className="w-5 h-5 text-[#AA6622] flex-shrink-0" />
                        <span className="text-[#4A4A4A]">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link 
                    to={`/leistungen/${service.id}`}
                    className="inline-flex items-center text-[#AA6622] font-bold hover:text-[#8e541b] transition-colors"
                  >
                    <span>Mehr zu {service.title}</span>
                    <ChevronRight className="w-5 h-5 ml-1" />
                  </Link>
                </div>
                <div className={`bg-[#F7F7F7] rounded-sm overflow-hidden ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  {service.image ? (
                    <img src={service.image} alt={service.title} className="w-full h-80 object-cover" />
                  ) : (
                    <div className="w-full h-80 flex items-center justify-center">
                      <service.icon className="w-32 h-32 text-[#AA6622]/20" />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#F7F7F7]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-black mb-6 font-['Outfit']">Welche Leistung benötigen Sie?</h2>
          <p className="text-lg text-[#4A4A4A] mb-8">
            Kontaktieren Sie uns für eine kostenlose Erstberatung. Wir helfen Ihnen, die richtige Lösung für Ihre Situation zu finden.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="tel:+491748936514" className="bg-[#AA6622] text-white px-8 py-4 rounded-sm font-bold flex items-center justify-center space-x-2 hover:bg-[#8e541b] transition-colors">
              <Phone className="w-5 h-5" /><span>0174 893 6514</span>
            </a>
            <Link to="/kontakt" className="border-2 border-black text-black px-8 py-4 rounded-sm font-bold flex items-center justify-center space-x-2 hover:bg-black hover:text-white transition-colors">
              <span>Kontaktformular</span><ChevronRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default LeistungenPage;
